/**
 * AgentMultiSelect — searchable checkbox dropdown for filtering a rep /
 * agent table down to one or more selected people. Used by the Rep
 * Performance report (Reports page), the Rep Activity rollup
 * (AdminRepActivity), and the Comms Dashboard rep table.
 *
 * Controlled component: the parent owns `selected` (array of ids) and gets
 * changes via `onChange`. Empty selection => caller shows all agents.
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Users, Search, Check, ChevronDown } from "lucide-react";

export function AgentMultiSelect({
  options = [],
  selected = [],
  onChange,
  testidPrefix = "agent-filter",
  placeholder = "Search agents…",
  allLabel = "All agents",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => (o.name || "").toLowerCase().includes(needle));
  }, [options, q]);

  const toggle = (id) => {
    if (selectedSet.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  const label = selected.length === 0
    ? allLabel
    : selected.length === 1
      ? (options.find((o) => o.id === selected[0])?.name || "1 agent")
      : `${selected.length} agents`;

  return (
    <div ref={ref} style={{ position: "relative" }} data-testid={testidPrefix}>
      <button
        type="button"
        data-testid={`${testidPrefix}-toggle`}
        onClick={() => setOpen((v) => !v)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, border: "0.5px solid #ddd", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontFamily: "inherit", background: selected.length ? "#EEF2FF" : "#fff", color: selected.length ? "#4338CA" : "#333", cursor: "pointer", fontWeight: selected.length ? 600 : 400, maxWidth: 200, whiteSpace: "nowrap" }}
      >
        <Users style={{ width: 13, height: 13 }} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
        <ChevronDown style={{ width: 13, height: 13, opacity: 0.6 }} />
      </button>
      {open && (
        <div
          data-testid={`${testidPrefix}-dropdown`}
          style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, width: 260, background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 10, boxShadow: "0 12px 32px -8px rgba(15,23,42,0.25)", zIndex: 6000, overflow: "hidden" }}
        >
          <div style={{ padding: 8, borderBottom: "0.5px solid #eee", display: "flex", alignItems: "center", gap: 6 }}>
            <Search style={{ width: 13, height: 13, color: "#94a3b8" }} />
            <input
              data-testid={`${testidPrefix}-search`}
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              style={{ border: "none", outline: "none", fontSize: 12, fontFamily: "inherit", width: "100%", background: "transparent" }}
            />
            {selected.length > 0 && (
              <button
                type="button"
                data-testid={`${testidPrefix}-clear`}
                onClick={() => onChange([])}
                style={{ background: "none", border: "none", color: "#5B6CFF", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
              >
                Clear
              </button>
            )}
          </div>
          <div style={{ maxHeight: 260, overflowY: "auto", padding: 4 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "16px 12px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>No agents match.</div>
            ) : (
              filtered.map((o) => {
                const on = selectedSet.has(o.id);
                return (
                  <button
                    type="button"
                    key={o.id}
                    data-testid={`${testidPrefix}-option-${o.id}`}
                    onClick={() => toggle(o.id)}
                    style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: on ? "#EEF2FF" : "transparent", border: "none", borderRadius: 6, padding: "7px 8px", fontSize: 12, fontFamily: "inherit", cursor: "pointer", color: "#1e293b" }}
                  >
                    <span style={{ width: 15, height: 15, borderRadius: 4, border: on ? "none" : "1.5px solid #cbd5e1", background: on ? "#5B6CFF" : "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {on && <Check style={{ width: 11, height: 11, color: "#fff" }} />}
                    </span>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.name}</span>
                    {o.role && <span style={{ fontSize: 10, color: "#94a3b8", textTransform: "capitalize" }}>{(o.role || "").replace(/_/g, " ")}</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AgentMultiSelect;
